import {AxiosInstance} from "axios";
import {createStreamingAdapter} from "@/core/http/streaming";
import {
    AssistantConstraint,
    AssistantMessageRequest,
    AssistantParameterValues,
    AssistantSettings,
    AssistantTurn,
    Proposition,
    PropositionPage
} from "@/ai-assistant/interfaces";

/**
 * Header carrying what a turn did, the propositions it produced above all.
 *
 * Beside the stream rather than inside it: the client collapses the events of
 * one chunk into a single one, so a second event type would either swallow the
 * end of an answer or merge with its terminator. The API names it in its CORS
 * `exposed_headers`, without which a browser would not hand it over at all.
 */
const TURN_HEADER = 'x-apodis-assistant-turn';

export interface AssistantSettingsPayload {
    values: AssistantParameterValues;
    typed_constraints: AssistantConstraint[];
}

/**
 * The turn record, when the response carried one and it could be read.
 *
 * A header the browser withheld, or one this client cannot parse, means the
 * answer stands alone: the text is still the answer, only the scenarios beside
 * it are missing.
 */
const readTurn = (headers: unknown): AssistantTurn | null => {
    const raw = typeof (headers as Headers)?.get === 'function'
        ? (headers as Headers).get(TURN_HEADER)
        : (headers as Record<string, string>)?.[TURN_HEADER];

    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw) as AssistantTurn;
    } catch {
        return null;
    }
};

export default function (client: AxiosInstance) {

    /**
     * Settings endpoint of one assistant within one pharmacy.
     */
    const settingsUrl = (organizationId: number, assistantKey: string): string =>
        `organizations/${organizationId}/ai-assistants/${assistantKey}/settings`;

    return {
        /**
         * The declared parameters of an assistant, alongside the values this
         * pharmacy saved and the defaults to fall back on.
         *
         * @param organizationId
         * @param assistantKey
         */
        async settings(organizationId: number, assistantKey: string): Promise<AssistantSettings> {
            const response = await client.get(settingsUrl(organizationId, assistantKey));

            return response.data.data;
        },

        /**
         * Save the values and the typed constraints, and read back what the
         * server kept.
         *
         * The whole settings object comes back rather than the values alone:
         * the conflicts are recomputed from the constraints, so a save is the
         * only moment they can change.
         *
         * @param organizationId
         * @param assistantKey
         * @param payload
         */
        async saveSettings(
            organizationId: number,
            assistantKey: string,
            payload: AssistantSettingsPayload
        ): Promise<AssistantSettings> {
            const response = await client.put(settingsUrl(organizationId, assistantKey), {data: payload});

            return response.data.data;
        },

        /**
         * Drop this pharmacy's values and read back the defaults.
         *
         * @param organizationId
         * @param assistantKey
         */
        async resetSettings(organizationId: number, assistantKey: string): Promise<AssistantSettings> {
            const response = await client.delete(settingsUrl(organizationId, assistantKey));

            return response.data.data;
        },

        /**
         * Ask the assistant and stream the answer.
         *
         * The wire format is the one the knowledge box already speaks, so the
         * chunks are parsed by the shared server-sent-events helpers.
         *
         * @param organizationId
         * @param assistantKey
         * @param request
         * @param onChunk
         * @param onError
         * @param abortController
         */
        async streamMessage(
            organizationId: number,
            assistantKey: string,
            request: AssistantMessageRequest,
            onChunk: (chunk: string) => void,
            onError: (error: unknown) => void,
            abortController?: AbortController
        ): Promise<AssistantTurn | null> {
            try {
                const response = await client({
                    method: "POST",
                    url: `organizations/${organizationId}/ai-assistants/${assistantKey}/messages`,
                    data: {data: request},
                    headers: {
                        'Accept': 'text/event-stream',
                    },
                    adapter: createStreamingAdapter(onChunk, onError),
                    signal: abortController?.signal
                });

                return readTurn(response.headers);
            } catch (error) {
                if (onError) {
                    onError(error);
                }

                return null;
            }
        },

        /**
         * One proposition, whole.
         *
         * Read by identifier rather than carried in the stream: the object is
         * large, it outlives the turn, and the pharmacist may reopen it long
         * after the conversation scrolled past.
         *
         * @param propositionId
         */
        async proposition(propositionId: number): Promise<Proposition> {
            const response = await client.get(`propositions/${propositionId}`);

            return response.data.data;
        },

        /**
         * Carry a scenario out.
         *
         * The application endpoint, never a tool of the model: the catalogue
         * exposed to it holds nothing that writes, and this call is precisely
         * what stays in the pharmacist's hands.
         *
         * Refused with a 409 when the planning moved since the scenario was
         * computed — a frequent case in an officine, where two people work in
         * parallel.
         *
         * @param propositionId
         */
        async applyProposition(propositionId: number): Promise<Proposition> {
            const response = await client.post(`propositions/${propositionId}/apply`);

            return response.data.data;
        },

        /**
         * The pharmacist said no. Nothing was written, nothing is undone — and
         * it is the product's main quality signal, so it must be easy to give.
         *
         * @param propositionId
         */
        async rejectProposition(propositionId: number): Promise<Proposition> {
            const response = await client.post(`propositions/${propositionId}/reject`);

            return response.data.data;
        },

        /**
         * Undo an application by replaying the inverse diff recorded when it
         * was applied.
         *
         * Refused with a 409 once the planning has moved again: the recorded
         * inverse describes the state as it was, and replaying it over a later
         * change would silently discard that change.
         *
         * @param propositionId
         */
        async cancelProposition(propositionId: number): Promise<Proposition> {
            const response = await client.post(`propositions/${propositionId}/cancel`);

            return response.data.data;
        },

        /**
         * One page of a pharmacy's propositions, most recent first.
         *
         * The page travels whole rather than as its rows alone: a list that
         * stops at twenty with nothing saying so looks complete, and the
         * pharmacist looking for last month's application would conclude it
         * was never made.
         *
         * @param organizationId
         * @param page
         * @param states Kept states; every state when empty.
         */
        async propositions(
            organizationId: number,
            page = 1,
            states: string[] = []
        ): Promise<PropositionPage> {
            const response = await client.get(`organizations/${organizationId}/propositions`, {
                params: {page, ...(states.length ? {states} : {})}
            });

            return response.data;
        }
    };
}
